// Canonical client-side Account-Manager display-name resolver.
//
// 2026-06-08 (Durable AM Display Fix): the denormalised
// `facilities.account_manager_name` column was retired. AM identity now lives
// solely on `facilities.account_manager_eid`; the display name is resolved from
// `employees` via that FK.
//
// Surfaces that read one of the `*_facilities` views (active_facilities,
// v_facilities_live, the incentive views) get the resolved name for free — the
// views LEFT JOIN the security-definer `v_am_name` resolver. This helper is for
// the handful of surfaces that must read the BASE `facilities` table directly
// (facility detail, closed-accounts archive, the account-level AM rollup,
// My Accounts' "show closed" branch).
//
// Why `v_am_name` and not `employees`: `public.employees` RLS is restrictive —
// account_manager / sales roles and the pre-sign-in anon demo cannot read the
// roster, so a direct `employees` join/fetch would return NULL names for them.
// `v_am_name` is security_invoker=false (runs as owner, bypasses employees RLS)
// and exposes only employee_id + display_name (already shown on facility cards),
// granted to anon + authenticated, so every caller resolves the same name.
// ============================================================================

import { supabase } from './supabase';

/**
 * Resolve a set of employee ids to display names via the canonical v_am_name
 * view. De-dupes and ignores null/empty ids. Returns a Map keyed by eid; eids
 * with no resolvable name are simply absent from the map.
 */
export async function fetchAmNameMap(
  eids: ReadonlyArray<string | null | undefined>,
): Promise<Map<string, string>> {
  const ids = Array.from(
    new Set(
      eids.filter((v): v is string => typeof v === 'string' && v.length > 0),
    ),
  );
  const map = new Map<string, string>();
  if (ids.length === 0) return map;

  const { data, error } = await supabase
    .from('v_am_name')
    .select('employee_id, display_name')
    .in('employee_id', ids);
  if (error) throw error;

  for (const r of (data ?? []) as {
    employee_id: string;
    display_name: string | null;
  }[]) {
    if (r.display_name) map.set(r.employee_id, r.display_name);
  }
  return map;
}
